/**
 * Utility for creating color ramps based on sequential color scales.
 * Provides linear interpolation between color stops over a numeric domain.
 */

/** HSL color channels for a palette stop. */
interface HslColor {
  /** Hue in degrees [0, 360). */
  h: number;
  /** Saturation percentage [0, 100]. */
  s: number;
  /** Lightness percentage [0, 100]. */
  l: number;
}

/** Plotly's Yellow-Orange-Red 9-color scale (YlOrRd9), expressed in HSL. */
const yellowOrangeRed9: readonly HslColor[] = [
  { h: 51, s: 90, l: 80 },
  { h: 48, s: 100, l: 78 },
  { h: 44, s: 99, l: 73 },
  { h: 34, s: 99, l: 65 },
  { h: 25, s: 98, l: 61 },
  { h: 10, s: 97, l: 58 },
  { h: 348, s: 100, l: 37 },
  { h: 342, s: 100, l: 25 }
];

/**
 * Creates a color ramp function that interpolates between color stops over a domain.
 * @param colorStops - HSL color stops defining the color scale.
 * @param domain - The [min, max] range for interpolation.
 * @returns A function that maps a number to an interpolated hsla() string.
 */
function createColorRamp(
  colorStops: readonly HslColor[],
  domain: [number, number]
) {
  const [domainMin, domainMax] = domain;
  const stopsCount = colorStops.length;

  return (value: number): string => {
    const normalizedValue = Math.min(
      1,
      Math.max(0, (value - domainMin) / (domainMax - domainMin))
    );
    const position = normalizedValue * (stopsCount - 1);
    const index = Math.floor(position);
    const fraction = position - index;

    if (index >= stopsCount - 1) {
      const last = colorStops[stopsCount - 1];
      return `hsla(${last.h}, ${last.s}%, ${last.l}%, 1)`;
    }

    const a = colorStops[index];
    const b = colorStops[index + 1];
    const h = Math.round(a.h + (b.h - a.h) * fraction);
    const s = Math.round(a.s + (b.s - a.s) * fraction);
    const l = Math.round(a.l + (b.l - a.l) * fraction);

    return `hsla(${h}, ${s}%, ${l}%, 1)`;
  };
}

/** Pre-configured color ramp for weeks (0-25) using Yellow-Orange-Red scale */
export const yellowOrangeRedPalette = createColorRamp(
  yellowOrangeRed9,
  [0, 25]
);
